import { CalendarClock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { blogPosts } from "@/data/blogData";

const BlogPost = ({ 
  title, 
  category, 
  excerpt, 
  date, 
  imageColor,
  slug
}: { 
  title: string, 
  category: string, 
  excerpt: string, 
  date: string,
  imageColor: string,
  slug: string
}) => {
  const { t } = useLanguage();
  
  return (
    <Link to={`/blog/${slug}`} className="soft-card group relative block overflow-hidden">
      <div className={`h-48 ${imageColor} flex items-center justify-center relative overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-slate-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="w-20 h-20 rounded-2xl bg-white/20 dark:bg-black/20 backdrop-blur-sm transform rotate-12 group-hover:rotate-45 transition-transform duration-300"></div>
      </div>
      <div className="p-8">
        <div className="flex justify-between items-center mb-4">
          <span className="px-4 py-1.5 rounded-full bg-blue-100/80 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 text-sm font-medium">
            {category}
          </span>
          <div className="flex items-center text-slate-500 dark:text-slate-400 text-sm">
            <CalendarClock className="w-4 h-4 mr-2" />
            {date}
          </div>
        </div>
        <h3 className="text-xl font-semibold mb-3 text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">{title}</h3>
        <p className="text-slate-600 dark:text-slate-300 mb-6 line-clamp-3">{excerpt}</p>
        <span className="inline-flex items-center text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 font-medium transition-colors">
          {t("read_more")} 
          <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </span>
      </div>
    </Link>
  );
};

const BlogSection = () => {
  const { t, language } = useLanguage();
  const currentPosts = blogPosts[language];

  return (
    <section id="blog" className="relative overflow-hidden py-24 sm:py-28">
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/8 dark:bg-blue-600/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-emerald-500/8 dark:bg-emerald-600/10 rounded-full blur-[120px]"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-14">
          <h2 className="section-heading mb-6">
            {t("blog_title")}
          </h2>
          <p className="section-subtitle max-w-3xl mx-auto">
            {t("blog_subtitle")}
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {currentPosts.slice(0, 3).map((post, index) => (
            <BlogPost
              key={index}
              title={post.title}
              category={post.category}
              excerpt={post.excerpt}
              date={post.date}
              imageColor={post.imageColor}
              slug={post.slug}
            />
          ))}
        </div>

        <div className="mt-12 text-center">
          <Button 
            asChild 
            variant="outline" 
            size="lg"
            className="rounded-full border border-slate-200 bg-white/80 text-slate-800 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-200 dark:hover:bg-slate-900 transition-all duration-300"
          >
            <Link to="/blog">{t("view_all_posts")}</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
